import { useCallback } from "react";
import { useImageSearchFlow } from "./image-search-flow";

// Pure session helpers live in `./image-search-session`. Re-export the readers
// for back-compat with any existing imports.
export { readSearchImage, readSearchImageBase64 } from "./image-search-session";

/**
 * Mini-App image-search entry-point hook — a port of the web
 * `useImageSearchPicker` (rewritten so upload no longer blocks navigation).
 *
 * `open()` now just opens the upload modal; the real pick / crop / upload happen
 * in the modal chain (<ImageSearchModals/>) + the catalog page (per-channel
 * upload). This fixes the old Mini flow where the camera button uploaded
 * synchronously with no captcha + no loading + no crop:
 *   - a slow / 4601-captcha-guarded / Taobao-500 upload left the user stranded
 *     on the page with no feedback (the catch only set an unread error state);
 *   - there was no region crop and no loading affordance at all.
 *
 * `isUploading` is always false (no synchronous wait here) and `error` always
 * null — kept so existing callers (`imgSearch.isUploading` / `imagePicker.open`
 * in index.tsx + catalog.tsx) compile unchanged.
 *
 * `channel` seeds the *initial* catalog tab only (default 1688 because Taobao's
 * upload adapter is flaky); the user can switch channels on the catalog.
 */
export function useImageSearchPicker(channel: "taobao" | "1688" = "1688") {
  const openUpload = useImageSearchFlow((s) => s.openUpload);
  const open = useCallback(() => {
    openUpload(channel);
  }, [channel, openUpload]);
  return { open, isUploading: false as boolean, error: null as string | null };
}
